import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Popover,
  Box,
  Typography,
  IconButton,
  Tabs,
  Tab,
  Button,
  Backdrop,
} from '@mui/material';
import { createPortal } from 'react-dom';
import {
  MarkEmailRead,
  Settings,
  Delete,
  Notifications,
} from '@mui/icons-material';

const NotificationPopover = ({
  open,
  anchorEl,
  onClose,
  announcements = [],
  title = 'Announcements',
  onMarkAsRead,
  onDelete,
  onEnablePushNotifications,
}) => {
  const [announcementTab, setAnnouncementTab] = useState(0);
  const [showPushNotifications, setShowPushNotifications] = useState(false);

  const unreadCount = announcements.filter((a) => !a.is_read).length;

  const handleMarkAsRead = (announcement) => {
    if (onMarkAsRead) {
      onMarkAsRead(announcement);
    }
  };

  const handleDelete = (announcement) => {
    if (onDelete) {
      onDelete(announcement);
    }
  };

  const handleEnablePushNotifications = () => {
    if (onEnablePushNotifications) {
      onEnablePushNotifications();
    } else {
      console.log('Enable push notifications');
    }
  };

  const displayedAnnouncements =
    announcementTab === 0
      ? announcements.filter((a) => !a.is_read)
      : announcements;

  return (
    <>
      {createPortal(
        <Backdrop
          open={open}
          onClick={onClose}
          sx={{
            zIndex: 1499,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          }}
        />,
        document.body
      )}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
        slotProps={{
          root: {
            sx: {
              zIndex: 1500,
            },
          },
        }}
        PaperProps={{
          sx: {
            width: 400,
            maxHeight: 600,
            ml: 1.5,
            borderRadius: '4px',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
            zIndex: 1500,
          },
        }}
      >
      <Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
          }}
        >
          <Typography
            sx={{
              fontSize: '1rem',
              fontWeight: 600,
              color: '#0F172A',
            }}
          >
            {title}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton size="small" sx={{ color: '#6b7280' }}>
              <MarkEmailRead fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              sx={{ color: '#6b7280' }}
              onClick={() => setShowPushNotifications(!showPushNotifications)}
            >
              <Settings fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {showPushNotifications && (
          <Box sx={{ p: 2, borderBottom: '1px solid rgba(0, 0, 0, 0.1)' }}>
            <Typography
              sx={{
                fontSize: '0.75rem',
                fontWeight: 400,
                color: '#6b7280',
                mb: 1.5,
              }}
            >
              Push Notifications
            </Typography>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Notifications sx={{ color: '#6366f1' }} />}
              onClick={handleEnablePushNotifications}
              sx={{
                borderColor: '#6366f1',
                color: '#6366f1',
                textTransform: 'none',
                fontSize: '0.875rem',
                fontWeight: 500,
                py: 1.5,
                borderRadius: '8px',
                '&:hover': {
                  borderColor: '#6366f1',
                  backgroundColor: 'rgba(99, 102, 241, 0.04)',
                },
              }}
            >
              ENABLE PUSH NOTIFICATIONS
            </Button>
          </Box>
        )}

        <Tabs
          value={announcementTab}
          onChange={(e, newValue) => setAnnouncementTab(newValue)}
          sx={{
            borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 500,
              minHeight: 48,
            },
          }}
        >
          <Tab
            label={`UNREAD (${unreadCount})`}
            sx={{
              color: announcementTab === 0 ? '#6366f1' : '#6b7280',
            }}
          />
          <Tab
            label="ALL"
            sx={{
              color: announcementTab === 1 ? '#6366f1' : '#6b7280',
            }}
          />
        </Tabs>

        <Box
          sx={{
            maxHeight: 400,
            overflowY: 'auto',
          }}
        >
          {displayedAnnouncements.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  color: '#6b7280',
                }}
              >
                No announcements
              </Typography>
            </Box>
          ) : (
            displayedAnnouncements.map((announcement) => (
              <Box
                key={announcement.id}
                sx={{
                  p: 2,
                  borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
                  '&:hover': {
                    backgroundColor: '#f9fafb',
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    mb: 1,
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        color: '#0F172A',
                        mb: 0.5,
                      }}
                    >
                      {announcement.title || 'Announcement'}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '0.75rem',
                        color: '#6b7280',
                        mb: 1,
                      }}
                    >
                      {announcement.description?.substring(0, 100)}
                      {announcement.description?.length > 100 ? '...' : ''}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '0.75rem',
                          color: '#9ca3af',
                        }}
                      >
                        {announcement.scheduled_datetime
                          ? new Date(announcement.scheduled_datetime).toLocaleDateString()
                          : ''}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton
                          size="small"
                          sx={{ color: '#6b7280' }}
                          onClick={() => handleMarkAsRead(announcement)}
                        >
                          <MarkEmailRead fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          sx={{ color: '#6b7280' }}
                          onClick={() => handleDelete(announcement)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            ))
          )}
        </Box>
      </Box>
    </Popover>
    </>
  );
};

NotificationPopover.propTypes = {
  open: PropTypes.bool.isRequired,
  anchorEl: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  announcements: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      title: PropTypes.string,
      description: PropTypes.string,
      scheduled_datetime: PropTypes.string,
      is_read: PropTypes.bool,
    })
  ),
  title: PropTypes.string,
  onMarkAsRead: PropTypes.func,
  onDelete: PropTypes.func,
  onEnablePushNotifications: PropTypes.func,
};

export default NotificationPopover;

